import { useEffect, useMemo, useState } from "react";
import { authedFetch } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { CreditCard, Gift, Loader2, Pencil, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const TIERS = ["SOLO", "CLINIC_LIGHT", "CLINIC_PLUS"];
const STATUSES = ["TRIAL", "FREE", "FREE_LIFETIME", "ACTIVE", "PAST_DUE", "CANCELLED", "PAUSED"];
const STATUS_TONE = {
  TRIAL: "bg-amber-50 text-amber-700 border-amber-200",
  FREE: "bg-sky-50 text-sky-700 border-sky-200",
  FREE_LIFETIME: "bg-indigo-50 text-indigo-700 border-indigo-200",
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PAST_DUE: "bg-rose-50 text-rose-700 border-rose-200",
  CANCELLED: "bg-slate-100 text-slate-600 border-slate-200",
  PAUSED: "bg-violet-50 text-violet-700 border-violet-200",
};
const fmtDate = (value) => value ? new Date(value).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";
const within = (value, range) => {
  if (range === "all") return true;
  if (!value) return false;
  const date = new Date(value);
  const now = new Date();
  if (range === "today") return date.toDateString() === now.toDateString();
  return date >= new Date(now.getTime() - Number(range) * 86400000);
};

export const AdminAccounts = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [sendingTestPrompt, setSendingTestPrompt] = useState(false);
  const [freeDays, setFreeDays] = useState("30");
  const [freeStart, setFreeStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [freeEnd, setFreeEnd] = useState("");
  const [granting, setGranting] = useState(false);
  const [grantingLifetime, setGrantingLifetime] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [sort, setSort] = useState("newest");
  const [statusFilter, setStatusFilter] = useState("all");
  const [accountView, setAccountView] = useState("regular");

  const load = async () => {
    const res = await authedFetch("/admin/accounts");
    setRows(res.ok ? await res.json() : []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const statusLabel = (row) => {
    if (row.subscription_status === "TRIAL") return row.trial_started_at ? "Trial active" : "Awaiting trial activation";
    if (row.subscription_status === "FREE") return "Complimentary access";
    if (row.subscription_status === "FREE_LIFETIME") return "Free for lifetime";
    if (row.subscription_status === "ACTIVE") return "Paid membership";
    return (row.subscription_status || "TRIAL").replaceAll("_", " ");
  };

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      const text = [row.account_name, row.billing_email, row.account_type, row.subscription_tier, row.subscription_status].filter(Boolean).join(" ").toLowerCase();
      return (accountView === "test" ? Boolean(row.is_test) : !row.is_test) &&
        (!needle || text.includes(needle)) && within(row.created_at, dateRange) &&
        (statusFilter === "all" ||
          (statusFilter === "AWAITING_TRIAL_ACTIVATION" ? row.subscription_status === "TRIAL" && !row.trial_started_at : row.subscription_status === statusFilter));
    }).sort((a, b) => {
      if (sort === "oldest") return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      if (sort === "newest") return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      return (a.account_name || "").localeCompare(b.account_name || "");
    });
  }, [rows, query, dateRange, sort, statusFilter, accountView]);

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      account_name: row.account_name || "",
      billing_email: row.billing_email || "",
      subscription_tier: row.subscription_tier || "SOLO",
      subscription_status: row.subscription_status || "TRIAL",
      is_test: !!row.is_test,
    });
    setFreeDays("30");
  };

  const save = async () => {
    setSaving(true);
    const response = await authedFetch("/admin/accounts/" + editing.id, {
      method: "PATCH",
      body: JSON.stringify({
        account_name: form.account_name,
        billing_email: form.billing_email,
        is_test: !!form.is_test,
        subscription_tier: form.subscription_tier,
        subscription_status: form.subscription_status,
      }),
    });
    const data = await response.json().catch(() => ({}));
    setSaving(false);
    if (!response.ok) {
      toast.error(data.detail || "Could not update the account or subscription. No changes were saved.");
      return;
    }
    toast.success("Account and subscription updated successfully.");
    setEditing(null);
    load();
  };

  const sendTestPrompt = async () => {
    setSendingTestPrompt(true);
    try {
      const response = await authedFetch("/admin/qa/availability-prompt/send-test", { method: "POST" });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "Test prompt could not be sent.");
      toast.success(`Availability prompt sent to ${data.to}.`);
    } catch (error) {
      toast.error(error.message || "Test prompt could not be sent.");
    } finally {
      setSendingTestPrompt(false);
    }
  };
  const grantFree = async () => {
    setGranting(true);
    const res = await authedFetch("/admin/accounts/" + editing.id + "/complimentary", {
      method: "POST",
      body: JSON.stringify(freeDays === "custom" ? { starts_at: freeStart, ends_at: freeEnd } : { days: Number(freeDays) }),
    });
    setGranting(false);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      toast.error(data.detail || "Could not grant complimentary access.");
      return;
    }
    const data = await res.json();
    toast.success(data.email_sent ? "Complimentary access added and email sent." : "Complimentary access added. No billing email was available.");
    setEditing(null);
    load();
  };

  const grantLifetime = async () => {
    if (!window.confirm("Make " + (editing.account_name || editing.billing_email) + " free for lifetime? Any active Stripe subscription will be cancelled and the account contact will be emailed.")) return;
    setGrantingLifetime(true);
    const res = await authedFetch("/admin/accounts/" + editing.id + "/lifetime-free", { method: "POST" });
    setGrantingLifetime(false);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      toast.error(data.detail || "Could not grant lifetime free access.");
      return;
    }
    const data = await res.json();
    toast.success(data.email_sent ? "Lifetime free access added and email sent." : "Lifetime free access added. No billing email was available.");
    setEditing(null);
    load();
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete ${editing.account_name}, its practice-manager sign-in and associated account record? This cannot be undone.`)) return;
    const res = await authedFetch("/admin/users/clinic/" + editing.id, { method: "DELETE" });
    if (!res.ok) return toast.error("Could not delete this clinic account.");
    toast.success("Clinic account permanently deleted.");
    setEditing(null); load();
  };

  return (
    <section data-testid="admin-accounts-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-slate-900"><CreditCard className="h-5 w-5 text-primary" /> Subscriptions</h2>
          <p className="mt-1 text-sm text-slate-500">View every account in one place and clearly distinguish paid memberships, trials awaiting launch, active trials and complimentary access.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setSearchOpen(!searchOpen)} title="Search accounts" aria-label="Search accounts">{searchOpen ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}</Button>
          {searchOpen && <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search account or email…" className="w-64" />}
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" aria-label="Filter signup date">
            <option value="all">Any signup date</option><option value="today">Today</option><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option>
          </select>
          <select value={accountView} onChange={(e) => setAccountView(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium" aria-label="Show regular or test subscription accounts">
            <option value="regular">Regular accounts</option><option value="test">Test accounts</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" aria-label="Filter status">
            <option value="all">All statuses</option><option value="AWAITING_TRIAL_ACTIVATION">Awaiting trial activation</option>{STATUSES.map((status) => <option key={status} value={status}>{status.replaceAll("_", " ")}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" aria-label="Sort accounts">
            <option value="newest">Newest signup</option><option value="oldest">Oldest signup</option><option value="name">Name A–Z</option>
          </select>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-md border border-slate-200 bg-white">
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading accounts…</div>
        ) : visible.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No accounts match these filters.</div>
        ) : (
          <table className="min-w-[900px] text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="min-w-56 px-4 py-3">Account</th><th className="min-w-64 px-4 py-3">Billing email</th><th className="px-4 py-3">Signed up</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right sticky right-0 bg-slate-50 border-l border-slate-200">Manage</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50">
                  <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-900">{row.account_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{row.billing_email || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-600">{fmtDate(row.created_at)}</td>
                  <td className="px-4 py-3 text-slate-600">{row.subscription_tier || "—"}</td>
                  <td className="px-4 py-3"><span className={"rounded-md border px-2 py-1 text-xs font-medium " + (STATUS_TONE[row.subscription_status] || STATUS_TONE.TRIAL)}>{statusLabel(row)}</span></td>
                  <td className="px-4 py-3 text-right sticky right-0 bg-white border-l border-slate-200"><Button variant="ghost" size="sm" onClick={() => openEdit(row)}><Pencil className="mr-1.5 h-4 w-4" /> Edit</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle>Manage account</DialogTitle><DialogDescription>Update billing details or grant a defined period of complimentary access.</DialogDescription></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label htmlFor="account-name">Account name</Label><Input id="account-name" value={form.account_name || ""} onChange={(e) => setForm({ ...form, account_name: e.target.value })} /></div>
            <div><Label htmlFor="billing-email">Billing email</Label><Input id="billing-email" type="email" value={form.billing_email || ""} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} /></div>
            <div><Label htmlFor="account-tier">Plan</Label><select id="account-tier" value={form.subscription_tier || "SOLO"} onChange={(e) => setForm({ ...form, subscription_tier: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">{TIERS.map((tier) => <option key={tier}>{tier}</option>)}</select></div>
            <div><Label htmlFor="account-status">Status</Label><select id="account-status" value={form.subscription_status || "TRIAL"} onChange={(e) => setForm({ ...form, subscription_status: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">{STATUSES.map((status) => <option key={status}>{status}</option>)}</select></div>
            <label className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 sm:col-span-2"><input type="checkbox" checked={!!form.is_test} onChange={(e) => setForm({ ...form, is_test: e.target.checked })} /> Test account (hidden from regular subscriptions)</label>
          </div>
          <Button onClick={save} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save account</Button>
          {editing?.id === "6e98d4d1-45e6-469e-b100-1596c9083427" && editing?.is_test && (
            <div className="rounded-md border border-slate-200 bg-white p-4">
              <p className="text-sm font-medium text-slate-900">QA availability prompt</p>
              <p className="mt-1 text-xs text-slate-600">Sends the normal monthly prompt to hello@simplyrefer.com.au for this test clinic only.</p>
              <Button type="button" variant="outline" className="mt-3" onClick={sendTestPrompt} disabled={sendingTestPrompt}>
                {sendingTestPrompt && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send test email now
              </Button>
            </div>
          )}
          <div className="rounded-md border border-sky-200 bg-sky-50 p-4">
            <p className="flex items-center gap-2 font-medium text-sky-900"><Gift className="h-4 w-4" /> Complimentary access</p>
            <p className="mt-1 text-xs text-sky-700">Sets the account to FREE for the selected period and automatically emails the billing address through Resend.</p>
            <div className="mt-3 flex gap-2">
              <select value={freeDays} onChange={(e) => setFreeDays(e.target.value)} className="h-9 flex-1 rounded-md border border-sky-200 bg-white px-3 text-sm" aria-label="Complimentary period">
                <option value="7">1 week</option><option value="14">2 weeks</option><option value="30">1 month</option><option value="90">3 months</option><option value="180">6 months</option><option value="365">1 year</option><option value="custom">Custom dates</option>
              </select>
              <Button type="button" onClick={grantFree} disabled={granting} className="bg-sky-700 hover:bg-sky-800">{granting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Grant &amp; email</Button>
            </div>
            <div className="mt-3 border-t border-sky-200 pt-3">
              <p className="text-xs text-sky-800">For permanent complimentary access, use the separate lifetime option. The account contact will receive confirmation and any active Stripe subscription will be cancelled.</p>
              <Button type="button" variant="outline" className="mt-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50" onClick={grantLifetime} disabled={grantingLifetime}>{grantingLifetime && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Make free for lifetime &amp; email</Button>
            </div>
            {freeDays === "custom" && (
              <div className="mt-3 grid gap-3 rounded-md border border-sky-200 bg-white p-3 sm:grid-cols-2">
                <div><Label htmlFor="free-start">Start date</Label><Input id="free-start" type="date" value={freeStart} onChange={(e) => setFreeStart(e.target.value)} /></div>
                <div><Label htmlFor="free-end">End date</Label><Input id="free-end" type="date" min={freeStart} value={freeEnd} onChange={(e) => setFreeEnd(e.target.value)} /></div>
              </div>
            )}
          </div>
          <Button type="button" variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" onClick={remove}><Trash2 className="mr-2 h-4 w-4" /> Permanently delete account</Button>
        </DialogContent>
      </Dialog>
    </section>
  );
};
